import { useEffect, useState } from 'react';
import { createCombinedPayment } from '../cabinet.api';
import { PaymentCreateResponse, SubscriptionPlan, WalletInfo } from '../cabinet.schemas';

type PaymentModalProps = {
  plan: SubscriptionPlan | null;
  wallet: WalletInfo | null;
  isOpen: boolean;
  onClose: () => void;
};

function getPlanTitle(plan: SubscriptionPlan | null) {
  return plan?.title || plan?.name || '';
}

function getPlanAmount(plan: SubscriptionPlan | null) {
  const value = plan?.price || plan?.amount || '';
  return String(value).replace(',', '.').replace(/[^\d.]/g, '');
}

function formatMoney(value: number) {
  return value.toFixed(2);
}

function formatRub(value: number | string) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '0 ₽';
  }

  return `${numericValue.toLocaleString('ru-RU', {
    maximumFractionDigits: numericValue % 1 === 0 ? 0 : 2,
    minimumFractionDigits: numericValue % 1 === 0 ? 0 : 2,
  })} ₽`;
}

export function PaymentModal({ plan, wallet, isOpen, onClose }: PaymentModalProps) {
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [useBonus, setUseBonus] = useState(false);
  const [bonusAmount, setBonusAmount] = useState('0.00');
  const [paymentInfo, setPaymentInfo] = useState<PaymentCreateResponse | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const selectedPrice = Number(getPlanAmount(plan));
  const walletBalance = Number(wallet?.balance || 0);
  const maxBonus = Math.min(walletBalance, selectedPrice * 0.5);
  const hasBonus = maxBonus > 0;

  useEffect(() => {
    if (isOpen) {
      setIsSuccess(false);
      setError('');
      setUseBonus(false);
      setBonusAmount('0.00');
      setPaymentInfo(null);
      setIsCreating(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const amount = getPlanAmount(plan);
  const normalizedBonusAmount = Math.min(Math.max(Number(bonusAmount) || 0, 0), maxBonus);
  const amountToPay = useBonus ? selectedPrice - normalizedBonusAmount : selectedPrice;

  const handleUseBonusChange = (enabled: boolean) => {
    setUseBonus(enabled);
    setBonusAmount(enabled ? formatMoney(maxBonus) : '0.00');
  };

  const handlePayment = async () => {
    if (isCreating) {
      return;
    }

    if (!plan?.id || !selectedPrice) {
      setError('Не удалось подготовить оплату');
      return;
    }

    if (useBonus && normalizedBonusAmount <= 0) {
      setError('Укажите сумму бонусов для списания');
      return;
    }

    const paymentWindow = window.open('', '_blank');

    setError('');
    setIsCreating(true);

    try {
      const payload = {
        subscription_id: plan.id,
        description: `Оплата подписки ${plan.slug || plan.id}`,
        metadatas: {
          slug: plan.slug || String(plan.id),
        },
        use_bonus: useBonus,
        ...(useBonus
          ? { bonus_amount: formatMoney(normalizedBonusAmount) }
          : { amount: formatMoney(selectedPrice) }),
      };

      const payment = await createCombinedPayment(payload);
      setPaymentInfo(payment);

      if (payment.confirmation_url) {
        if (paymentWindow) {
          paymentWindow.location.href = payment.confirmation_url;
        } else {
          window.location.href = payment.confirmation_url;
        }
      } else if (paymentWindow) {
        paymentWindow.close();
      }

      setIsSuccess(true);
      window.dispatchEvent(new Event('mifvpn:refresh'));
    } catch {
      if (paymentWindow) {
        paymentWindow.close();
      }

      setError('Не удалось создать платёж. Попробуй ещё раз.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="cabinet-modal-overlay" role="presentation" onMouseDown={onClose}>
      <section className="cabinet-modal" role="dialog" aria-modal="true" aria-labelledby="payment-title" onMouseDown={(event) => event.stopPropagation()}>
        <button className="cabinet-modal-close" type="button" aria-label="Закрыть оплату" onClick={onClose}>x</button>
        <div className="payment-modal-head">
          <span>Оформление тарифа</span>
          <h2 id="payment-title">Оплата подписки</h2>
        </div>

        <div className="payment-plan-summary">
          <div>
            <span>Выбранный тариф</span>
            <strong>{getPlanTitle(plan)}</strong>
          </div>
          <b>{formatRub(amount)}</b>
        </div>

        <label className={`payment-bonus-toggle${useBonus ? ' active' : ''}${!hasBonus ? ' disabled' : ''}`}>
          <span className="payment-check">
            <input
              checked={useBonus}
              disabled={!hasBonus}
              type="checkbox"
              onChange={(event) => handleUseBonusChange(event.target.checked)}
            />
            <i aria-hidden="true" />
          </span>
          <span>
            <strong>Списать бонусы</strong>
            <small>
              {hasBonus
                ? `Доступно ${formatRub(walletBalance)}, можно списать до ${formatRub(maxBonus)}`
                : 'На балансе пока нет бонусов для списания'}
            </small>
          </span>
        </label>

        {useBonus ? (
          <div className="payment-bonus-field">
            <label htmlFor="bonus-amount">Сумма бонусов</label>
            <div>
              <input
                id="bonus-amount"
                max={maxBonus}
                min={0}
                step="0.01"
                type="number"
                value={bonusAmount}
                onBlur={() => setBonusAmount(formatMoney(normalizedBonusAmount))}
                onChange={(event) => setBonusAmount(event.target.value)}
              />
              <button type="button" onClick={() => setBonusAmount(formatMoney(maxBonus))}>Максимум</button>
            </div>
          </div>
        ) : null}

        <div className="payment-breakdown" aria-label="Расчет платежа">
          <div className="payment-line">
            <span>Стоимость тарифа</span>
            <strong>{formatRub(selectedPrice)}</strong>
          </div>
          <div className="payment-line bonus">
            <span>Бонусами</span>
            <strong>-{formatRub(useBonus ? normalizedBonusAmount : 0)}</strong>
          </div>
          <div className="payment-total">
            <span>К оплате</span>
            <strong>{formatRub(amountToPay)}</strong>
          </div>
        </div>

        <button className="button button-primary payment-primary-action" disabled={isCreating} type="button" onClick={handlePayment}>
          {isCreating ? 'Создаем платеж...' : 'Перейти к оплате'}
        </button>

        {error ? <div className="cabinet-modal-error">{error}</div> : null}
        {isSuccess ? (
          <div className="cabinet-success">
            <h3>Платеж создан</h3>
            <p>К оплате: {formatRub(paymentInfo?.amount_to_pay || amountToPay)}. Окно подтверждения открыто отдельно.</p>
          </div>
        ) : null}
      </section>
    </div>
  );
}
